import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Delete02Icon, DragDropVerticalIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Collapse } from 'react-bootstrap';
import { useFieldArray } from 'react-hook-form';

import { Accordion } from 'components/Accordion/Accordion';
import { Input, ToggleLabel } from 'components/FormComponents';
import { Button } from 'components/FormComponents';

import styles from './MultipleChoiceSettings.module.scss';

const settingsHeader = <span className="text-neutral-7 text-xl font-medium">Settings</span>;

export const MultipleChoiceSettings = ({ register, control, name, field, errors }) => {
  return (
    <>
      <div className="flex flex-col overflow-hidden u-margin-top">
        <div className="flex flex-col gap-4">
          <OptionsConfig register={register} name={name} control={control} errors={errors} />
          <Accordion defaultCollapsed={false} header={settingsHeader}>
            <div className="border border-neutral-4 rounded-2xl overflow-hidden">
              <div className="p-2 border-b border-neutral-4">
                <ToggleLabel register={register} name={`${name}.required`} label="Required" />
              </div>
              <div className="p-2 border-b border-neutral-4">
                <ToggleLabel
                  register={register}
                  name={`${name}.use_description`}
                  label="Add description"
                />
                <Collapse in={field.use_description}>
                  <div>
                    <Input
                      register={register}
                      name={`${name}.description`}
                      className="u-margin-top--small"
                      placeholder="Enter description..."
                    />
                  </div>
                </Collapse>
              </div>
              <div className="p-2 border-b border-neutral-4">
                <ToggleLabel
                  register={register}
                  name={`${name}.options.multiple_selection`}
                  label="Multiple selection"
                />
              </div>
              <div className="p-2">
                <ToggleLabel
                  register={register}
                  name={`${name}.options.other_option`}
                  label="Add 'other' option"
                />
              </div>
            </div>
          </Accordion>
        </div>
      </div>
    </>
  );
};

const OptionsConfig = ({ register, name, control, errors }) => {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `${name}.options.options`,
  });
  var errorCount = 0;
  if (fields?.length && errors?.options?.options?.length) {
    errors?.options?.options?.forEach(error => {
      if (error) {
        errorCount = errorCount + 1;
      }
    });
  }

  const addOption = () => {
    append({ value: '' });
  };

  const onDragEnd = ({ source, destination }) => {
    if (!source || !destination) return;
    move(source.index, destination.index);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="options">
        {({ innerRef, droppableProps, placeholder }) => (
          <div
            className="card card-with-border surface-secondary pl-4 pr-6"
            {...droppableProps}
            ref={innerRef}>
            {fields.map((option, index) => (
              <Option
                key={option.id}
                index={index}
                name={`${name}.options.options.${index}`}
                register={register}
                option={option}
                onRemove={() => remove(index)}
                onEnter={addOption}
                errors={errors}
              />
            ))}
            {placeholder}
            <Button type="link" onClick={addOption} className="text-lg">
              + Add another option
            </Button>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const Option = ({ name, register, onRemove, option, index, onEnter, errors }) => {
  const handleKeyDown = e => {
    switch (e.code) {
      case 'Enter':
        e.preventDefault();
        onEnter();
        break;
      case 'Backspace':
        if (e.target.value === '') {
          e.preventDefault();
          onRemove();
        }
        break;
    }
  };

  return (
    <Draggable draggableId={option.id} index={index}>
      {({ draggableProps, dragHandleProps, innerRef }) => (
        <div
          className={cn(styles.option, 'd-flex', 'align-items-center', 'u-margin-top')}
          {...draggableProps}
          {...dragHandleProps}
          ref={innerRef}>
          <HugeiconsIcon
            icon={DragDropVerticalIcon}
            strokeWidth={2}
            size="24"
            className="text-neutral-6"
          />
          <Input
            register={register}
            name={`${name}.value`}
            className={styles.input}
            validators={{ required: true }}
            error={errors?.options?.options?.[index]?.value?.type}
            placeholder="Enter option..."
            actionIcon={Delete02Icon}
            newIconLibrary
            onActionClick={onRemove}
            inputProps={{
              onKeyDown: handleKeyDown,
            }}
          />
        </div>
      )}
    </Draggable>
  );
};
